import { isDefined, indexBy } from 'remeda'
import { FairlyPrioritizedUsers } from '@chilipiper/api-client/src/distribution'
import {
  AssignmentRequest,
  AssignmentRequestAssignmentWithKnownAssigneeRequest,
  OwnershipAssigneeEvaluationV3,
  PublicOwnershipSchedulingLink,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  OwnershipSchedulingLinkAssignments,
  RoundRobinAvailableAttendees,
  RoundRobinExpectedHostV2,
  RoundRobinSchedulingLinkAssignment,
  StrictRoundRobin1,
} from '@chilipiper/api-client/src/user-availability'
import { UsersAvailability, MultiRoundRobinAvailability } from '../../load-availability-options'
import {
  OwnershipLinkRoutingResult,
  isOwnerFromSimpleRule,
  isMixedResults,
  isOwner,
  isDistributionUsers,
  isWithDistribution,
  MixedResult,
  WithDistribution,
} from '../types'

export const mapOwnershipLinkRoutingResultToAssignments = (
  strictAssignees?: StrictRoundRobin1[],
  routingResult?: OwnershipLinkRoutingResult
): AssignmentRequestAssignmentWithKnownAssigneeRequest[] => {
  if (!routingResult) {
    return []
  }

  const mappedAvailableAssignees = indexBy(strictAssignees || [], item => item.distributionId)

  if (isMixedResults(routingResult)) {
    return routingResult.result.flatMap(item => {
      if (isOwner(item)) {
        return {
          assigneeId: item.ownerId,
          teamId: item.teamId,
          rule: {
            ruleId: item.ruleId,
            type: 'SharedRule' as const,
          },
          type: 'AssignedViaTeam' as const,
        }
      }

      if (isDistributionUsers(item)) {
        const availableAssignee = mappedAvailableAssignees[item.id]
        if (!availableAssignee) {
          return []
        }
        return {
          assigneeId: availableAssignee.userId,
          distributionId: item.id,
          type: 'AssignedViaDistribution' as const,
        }
      }

      return []
    })
  }

  if (isOwnerFromSimpleRule(routingResult)) {
    return [
      {
        assigneeId: routingResult.ownerId,
        teamId: routingResult.teamId,
        rule: {
          type: 'SimpleRule' as const,
        },
        type: 'AssignedViaTeam' as const,
      },
    ]
  }

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions.flatMap(distribution => {
      const availableAssignee = mappedAvailableAssignees[distribution.id]
      if (!availableAssignee) {
        return []
      }
      return {
        assigneeId: availableAssignee.userId,
        distributionId: distribution.id,
        type: 'AssignedViaDistribution' as const,
      }
    })
  }

  return []
}
//TODO: handle better name
export const mapOwnershipLinkRoutingResultToAssignments2 = (
  rrAssignees?: RoundRobinAvailableAttendees[],
  routingResult?: OwnershipLinkRoutingResult
): AssignmentRequest[] => {
  if (!routingResult) {
    return []
  }
  //TOOD: handle optional assignee
  const mappedAvailableAssignees = indexBy(rrAssignees || [], item => item.distributionId)

  if (isMixedResults(routingResult)) {
    return routingResult.result.flatMap(item => {
      if (isOwner(item)) {
        return {
          assigneeId: item.ownerId,
          teamId: item.teamId,
          rule: {
            ruleId: item.ruleId,
            type: 'SharedRule' as const,
          },
          type: 'AssignedViaTeam' as const,
        }
      }

      if (isDistributionUsers(item)) {
        const availableAssignee = mappedAvailableAssignees[item.id]
        if (!availableAssignee) {
          return []
        }

        if (availableAssignee.type === 'StrictRoundRobin') {
          return {
            assigneeId: availableAssignee.userId,
            distributionId: item.id,
            type: 'AssignedViaDistribution' as const,
          }
        } else {
          return {
            availableUsers: availableAssignee.userIds,
            distributionId: item.id,
            type: 'WithUnresolvedDistribution' as const,
          }
        }
      }

      return []
    })
  }

  if (isOwnerFromSimpleRule(routingResult)) {
    return [
      {
        assigneeId: routingResult.ownerId,
        teamId: routingResult.teamId,
        rule: {
          type: 'SimpleRule' as const,
        },
        type: 'AssignedViaTeam' as const,
      },
    ]
  }

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions.flatMap(distribution => {
      const availableAssignee = mappedAvailableAssignees[distribution.id]
      if (!availableAssignee) {
        return []
      }

      if (availableAssignee.type === 'StrictRoundRobin') {
        return {
          assigneeId: availableAssignee.userId,
          distributionId: distribution.id,
          type: 'AssignedViaDistribution' as const,
        }
      } else {
        return {
          availableUsers: availableAssignee.userIds,
          distributionId: distribution.id,
          type: 'WithUnresolvedDistribution' as const,
        }
      }
    })
  }

  return []
}

export const getFirstAssignmentFromRoutingResult = (
  routingResult: OwnershipLinkRoutingResult
): { userId: string } | { distributionId: string } => {
  // Handle OwnerFromSimpleRule - return the owner
  if (isOwnerFromSimpleRule(routingResult)) {
    return { userId: routingResult.ownerId }
  }

  // Handle MixedResults - return first assignment (owner or distribution)
  if (isMixedResults(routingResult) && routingResult.result.length > 0) {
    const firstItem = routingResult.result[0]

    if (isOwner(firstItem)) {
      return { userId: firstItem.ownerId }
    }

    if (isDistributionUsers(firstItem)) {
      return { distributionId: firstItem.id }
    }
  }

  // Handle WithDistribution - return first distribution
  if (isWithDistribution(routingResult) && routingResult.distributions.length > 0) {
    return { distributionId: routingResult.distributions[0].id }
  }

  throw new Error('No first assignment found')
}

type DistributionUsers = FairlyPrioritizedUsers & { id: string }

export const getOwnerIdsFromRoutingResult = (
  routingResult?: OwnershipLinkRoutingResult
): string[] => {
  if (!routingResult) return []

  if (isOwnerFromSimpleRule(routingResult)) {
    return [routingResult.ownerId]
  }

  if (isMixedResults(routingResult)) {
    const ownerIds: string[] = []
    routingResult.result.forEach(item => {
      if (isOwner(item)) {
        ownerIds.push(item.ownerId)
      }
    })
    return ownerIds
  }

  return []
}

/** Get distributions from OwnershipLinkRoutingResult */
const getDistributionsFromRoutingResult = (
  routingResult?: OwnershipLinkRoutingResult
): DistributionUsers[] => {
  if (!routingResult) return []

  if (isWithDistribution(routingResult)) {
    return routingResult.distributions
  }

  if (isMixedResults(routingResult)) {
    const distributions: DistributionUsers[] = []
    routingResult.result.forEach(item => {
      if (isDistributionUsers(item)) {
        distributions.push(item)
      }
    })
    return distributions
  }

  return []
}

export const getRoutingResultAvailabilityOptions = ({
  routingResult,
  expectedHost,
  additionalRequiredAttendees,
}: {
  routingResult?: OwnershipLinkRoutingResult
  expectedHost?: RoundRobinExpectedHostV2 // todo: check why we need to pass it around?
  additionalRequiredAttendees?: string[]
}): UsersAvailability | MultiRoundRobinAvailability | undefined => {
  if (!routingResult || !expectedHost) {
    return undefined
  }

  const owners = getOwnerIdsFromRoutingResult(routingResult)
  const distributions = getDistributionsFromRoutingResult(routingResult)

  if (isOwnerFromSimpleRule(routingResult)) {
    return {
      userIds: [routingResult.ownerId],
      expectedHost:
        expectedHost.type === 'User'
          ? expectedHost
          : { type: 'User', userId: routingResult.ownerId },
      additionalRequiredAttendees,
      meetingLimit: {
        type: 'NoMeetingLimit' as const,
      },
    } satisfies UsersAvailability
  }

  if (isMixedResults(routingResult)) {
    if (distributions.length === 0 && owners.length > 0) {
      return {
        userIds: owners,
        expectedHost:
          expectedHost.type === 'User' ? expectedHost : { type: 'User', userId: owners[0] },
        additionalRequiredAttendees,
        meetingLimit: {
          type: 'NoMeetingLimit' as const,
        },
      } satisfies UsersAvailability
    }

    // If has distributions (with or without owners)
    if (distributions.length > 0) {
      // Get attendees from distributions based on their users
      const distributionAttendees = distributions.map(dist => ({
        userIds: dist.users || [],
        distributionId: dist.id,
      }))

      const additionalAttendees = [...(additionalRequiredAttendees ?? []), ...owners]

      return {
        distributionAttendees,
        expectedHost,
        additionalRequiredAttendees: additionalAttendees.length ? additionalAttendees : undefined,
        ownershipAndUsersAttendees: owners,
      } satisfies MultiRoundRobinAvailability
    }
  }

  // Handle WithDistribution - only distributions
  if (isWithDistribution(routingResult) && routingResult.distributions.length > 0) {
    const distributionAttendees = routingResult.distributions.map(dist => ({
      userIds: dist.users || [],
      distributionId: dist.id,
    }))

    return {
      distributionAttendees,
      expectedHost,
      additionalRequiredAttendees,
      ownershipAndUsersAttendees: [],
    } satisfies MultiRoundRobinAvailability
  }

  return undefined
}

export const transformOwnershipAssignmentsToRoutingResult = (
  assignments?: OwnershipSchedulingLinkAssignments,
  distributions?: DistributionUsers[],
  ownershipLink?: Pick<PublicOwnershipSchedulingLink, 'distributionIds'>,
  assigneeEvaluationV3?: OwnershipAssigneeEvaluationV3
): OwnershipLinkRoutingResult | undefined => {
  if (!assignments) return undefined

  if (assignments.type === 'WithLegacySingleDistribution') {
    if (!ownershipLink?.distributionIds?.length) {
      return undefined
    }

    // Map distributions in the order defined by ownershipLink.distributionIds
    const orderedDistributions = ownershipLink.distributionIds
      .map(distId => distributions?.find(d => d.id === distId))
      .filter(isDefined)

    if (orderedDistributions.length === 0) {
      return undefined
    }

    const assigneeId = assignments.assigneeId
    const firstDistribution = orderedDistributions[0]

    // If assignee is specified and is in the first distribution, update it
    if (assigneeId && firstDistribution.users?.includes(assigneeId)) {
      const [first, ...rest] = orderedDistributions
      return {
        distributions: [{ ...first, users: [assigneeId] }, ...rest],
      }
    }

    // Otherwise return ordered distributions as is
    return {
      distributions: orderedDistributions,
    }
  }

  if (assignments.type === 'WithSimpleRule') {
    return {
      ownerId: assignments.assigneeId,
      teamId: assignments.teamId,
    }
  }

  if (assignments.type === 'WithLegacySimpleRule') {
    if (
      assigneeEvaluationV3?.type === 'MixedRoutingResults' &&
      assigneeEvaluationV3.results?.length > 0
    ) {
      const firstResult = assigneeEvaluationV3.results[0]
      if (firstResult.type !== 'Distributed' && firstResult.teamRef?.teamId) {
        return {
          result: [
            {
              ownerId: assignments.assigneeId,
              teamId: firstResult.teamRef.teamId,
              ruleId: firstResult.ruleId,
            },
          ],
        }
      }
    } else if (assigneeEvaluationV3?.type === 'SimpleRuleResult') {
      return {
        ownerId: assignments.assigneeId,
        teamId: assigneeEvaluationV3.teamRef.teamId,
      }
    }

    // If we can't determine the team, return undefined
    return undefined
  }

  if (assignments.type === 'WithMixedRouting' && assignments.assignments) {
    const results: MixedResult[] = []

    assignments.assignments.forEach(assignment => {
      if (assignment.type === 'FromDistributionAssignment') {
        const distribution = distributions?.find(d => d.id === assignment.distributionId)
        if (distribution) {
          // If assignment has an assigneeId, check if it's in the distribution
          if (assignment.assigneeId && distribution.users?.includes(assignment.assigneeId)) {
            // Create a distribution with only the assigned user
            results.push({
              ...distribution,
              users: [assignment.assigneeId],
            })
          } else if (!assignment.assigneeId) {
            // If no specific assignee, include the whole distribution
            results.push(distribution)
          }
        }
      } else if (assignment.type === 'FromOwnershipRuleAssignment') {
        results.push({
          ownerId: assignment.assigneeId,
          teamId: assignment.teamId,
          ruleId: assignment.rule,
        })
      }
    })

    if (results.length > 0) {
      return {
        result: results,
      }
    }
  }

  return undefined
}

export const transformAssigneeEvaluationToRoutingResult = (
  assigneeEvaluation?: OwnershipAssigneeEvaluationV3,
  distributions?: DistributionUsers[]
): OwnershipLinkRoutingResult | undefined => {
  if (!assigneeEvaluation) {
    // If no assigneeEvaluation but we have distributions, return WithDistribution
    if (distributions && distributions.length > 0) {
      return {
        distributions: distributions,
      }
    }
    return undefined
  }

  switch (assigneeEvaluation.type) {
    case 'Distributed': {
      const distirbutionsMapped = assigneeEvaluation.distributionIds
        ?.map(distributionId => {
          const distribution = distributions?.find(d => d.id === distributionId)
          return distribution
        })
        .filter(isDefined)
      return {
        distributions: distirbutionsMapped || [],
      }
    }

    case 'MixedRoutingResults': {
      const mixedResults: MixedResult[] = assigneeEvaluation.results
        ?.map(result => {
          if (result.type === 'Distributed') {
            const distribution = distributions?.find(d => d.id === result.distributionId)
            return distribution
          } else
            return {
              ownerId: result.ownerId,
              teamId: result.teamRef.teamId,
              ruleId: result.ruleId,
            }
        })
        .filter(isDefined)

      return {
        result: mixedResults,
      }
    }

    case 'SimpleRuleResult': {
      return {
        ownerId: assigneeEvaluation.ownerId,
        teamId: assigneeEvaluation.teamRef.teamId,
      }
    }

    default:
      return undefined
  }
}

export const getDistributionIdsFromRoundRobinAssignments = (
  assignments?: RoundRobinSchedulingLinkAssignment[],
  linkDistributionIds?: string[]
): string[] => {
  // If no assignments, fallback to link's distributionIds
  if (!assignments || assignments.length === 0) {
    return linkDistributionIds || []
  }

  // Check if there's any LegacyAssignment
  const hasLegacyAssignment = assignments.some(a => a.type === 'LegacyAssignment')

  // If there's any LegacyAssignment, return the full link's distributionIds
  if (hasLegacyAssignment) {
    return linkDistributionIds || []
  }

  // Otherwise, collect distributionIds from Assignment types
  const distributionIds: string[] = []

  assignments.forEach(assignment => {
    if (assignment.type === 'Assignment' && assignment.distributionId) {
      if (!distributionIds.includes(assignment.distributionId)) {
        distributionIds.push(assignment.distributionId)
      }
    }
  })

  // If no distribution IDs found in assignments, fallback to link's distributionIds
  return distributionIds.length > 0 ? distributionIds : linkDistributionIds || []
}

export const transformRoundRobinAssignmentsToRoutingResult = (
  assignments?: RoundRobinSchedulingLinkAssignment[],
  distributions?: DistributionUsers[] // should be ordered like in the link
): WithDistribution | undefined => {
  if (!distributions || distributions.length === 0) {
    return undefined
  }

  // If no assignments provided, return all distributions
  if (!assignments || assignments.length === 0) {
    return {
      distributions: distributions,
    }
  }

  // Check if there's any LegacyAssignment
  const hasLegacyAssignment = assignments.some(a => a.type === 'LegacyAssignment')

  // If there's any LegacyAssignment, return all distributions
  if (hasLegacyAssignment) {
    // Check if there's a specific assigneeId in the legacy assignment
    // there never be multiple LegacyAssignment in the array
    const legacyAssignment = assignments.find(a => a.type === 'LegacyAssignment')
    if (legacyAssignment?.assigneeId && distributions.length > 0) {
      const [first, ...rest] = distributions
      if (first.users?.includes(legacyAssignment.assigneeId)) {
        return {
          distributions: [{ ...first, users: [legacyAssignment.assigneeId] }, ...rest],
        }
      }
    }

    return {
      distributions: distributions,
    }
  }

  // Handle new Assignment types
  const distributionsList: DistributionUsers[] = []

  assignments.forEach(assignment => {
    if (assignment.type === 'Assignment' && assignment.distributionId) {
      const distribution = distributions.find(d => d.id === assignment.distributionId)
      if (distribution) {
        // If assignment has specific assigneeId, narrow the distribution to that user
        if (assignment.assigneeId && distribution.users?.includes(assignment.assigneeId)) {
          distributionsList.push({
            ...distribution,
            users: [assignment.assigneeId],
          })
        } else {
          // No specific assignee, use the full distribution
          distributionsList.push(distribution)
        }
      }
    }
  })

  // Return the distributions we found
  return distributionsList.length > 0 ? { distributions: distributionsList } : undefined
}

export const getDistributionIdsFromAssigneeEvaluation = (
  assigneeEvaluation?: OwnershipAssigneeEvaluationV3
): string[] => {
  if (!assigneeEvaluation) return []

  switch (assigneeEvaluation.type) {
    case 'Distributed': {
      return assigneeEvaluation.distributionIds || []
    }

    case 'MixedRoutingResults': {
      const distributionIds: string[] = []

      assigneeEvaluation.results?.forEach(result => {
        if (result.type === 'Distributed' && result.distributionId) {
          distributionIds.push(result.distributionId)
        }
      })

      return distributionIds
    }

    case 'SimpleRuleResult': {
      return []
    }

    default:
      return []
  }
}

/**
 * Applies selected assignees to OwnershipLinkRoutingResult. Supports both WithDistribution and
 * MixedResults types.
 *
 * @param routingResult - The routing result (WithDistribution, MixedResults, or
 *   OwnerFromSimpleRule)
 * @param selectedAssigneeIds - Array of assignee IDs indexed by distribution position
 * @returns Updated routing result with assignees applied, or undefined
 */
export const applySelectedAssigneesToRoutingResult = (
  routingResult?: OwnershipLinkRoutingResult,
  selectedAssigneeIds?: string[]
): OwnershipLinkRoutingResult | undefined => {
  if (!routingResult || !selectedAssigneeIds || selectedAssigneeIds.length === 0) {
    return routingResult
  }

  // Handle WithDistribution type
  if (isWithDistribution(routingResult)) {
    return applySelectedAssigneesToWithDistribution(routingResult, selectedAssigneeIds)
  }

  // Handle MixedResults type
  if (isMixedResults(routingResult) && routingResult.result.length > 0) {
    let distributionIndex = 0
    const updatedResult = routingResult.result.map(item => {
      // Only process distribution items, skip owners
      if (isDistributionUsers(item)) {
        const assigneeId = selectedAssigneeIds[distributionIndex]
        distributionIndex++

        if (assigneeId && item.users?.includes(assigneeId)) {
          // Update distribution to only include the selected assignee
          return {
            ...item,
            users: [assigneeId],
          }
        }
      }

      // Return unchanged for owners or if no assignee match
      return item
    })

    return {
      result: updatedResult,
    }
  }

  // Return unchanged for OwnerFromSimpleRule (no distributions to apply assignees to)
  return routingResult
}

/**
 * Applies selected assignees to WithDistribution routing result. Matches assignees to distributions
 * by index.
 *
 * @param routingResult - The routing result with distributions
 * @param selectedAssigneeIds - Array of assignee IDs indexed by distribution position
 * @returns Updated routing result with assignees applied, or undefined
 */
export const applySelectedAssigneesToWithDistribution = (
  routingResult?: WithDistribution,
  selectedAssigneeIds?: string[]
): WithDistribution | undefined => {
  if (!routingResult || !selectedAssigneeIds || selectedAssigneeIds.length === 0) {
    return routingResult
  }

  // WithDistribution always has distributions array
  if (routingResult.distributions && routingResult.distributions.length > 0) {
    const updatedDistributions = routingResult.distributions.map((distribution, index) => {
      // Check if we have an assignee for this distribution index
      const assigneeId = selectedAssigneeIds[index]

      if (!assigneeId) {
        // No assignee for this distribution, return unchanged
        return distribution
      }

      // Check if assigneeId is in this distribution's users
      const isAssigneeInDistribution = distribution.users?.includes(assigneeId)

      if (isAssigneeInDistribution) {
        // Update distribution to only include the selected assignee
        return {
          ...distribution,
          users: [assigneeId],
        }
      }
      return distribution
    })

    return {
      distributions: updatedDistributions,
    }
  }

  // Return unchanged if no distributions
  return routingResult
}

export const getDistributionIdsFromOwnershipAssignments = (
  assignments?: OwnershipSchedulingLinkAssignments,
  linkDistributionIds?: string[]
): string[] => {
  if (!assignments) return []

  if (assignments.type === 'WithLegacySingleDistribution') {
    return linkDistributionIds || []
  }

  if (assignments.type === 'WithMixedRouting' && assignments.assignments) {
    const distributionIds: string[] = []
    assignments.assignments.forEach(assignment => {
      if (assignment.type === 'FromDistributionAssignment' && assignment.distributionId) {
        distributionIds.push(assignment.distributionId)
      }
    })
    return distributionIds
  }

  return []
}
